import React from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router';

import { Button, FullscreenLoader } from 'src/components/common';
import { StripeCustomerPaymentMethodsList } from 'src/components/stripe/StripeCustomerPaymentMethodsList';
import { ModalId } from 'src/constants/modals';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser } from 'src/hooks/user';
import { useCurrentUserVerifierStaff } from 'src/hooks/verifierStaff';
import { VerifierStaffAddPaymentMethodModal } from 'src/modals/billing';
import { SxProp } from 'src/types/mui';

import { ActiveBillingRateItem } from './ActiveBillingRateItem';
import { getActiveBillingRatesData } from './utils/buildActiveBillingRatesData';

export const ActiveBillingRates: React.FC = () => {
  const location = useLocation();

  const { isUserLoading } = useCurrentUser();
  const { verifierStaff, verifierStaffBillingInfo, billingInfoDataLoading } =
    useCurrentUserVerifierStaff();
  const { openModal } = useModal(ModalId.StripeAddPaymentMethodModal);

  const handleAddButtonClick = React.useCallback(() => {
    openModal(ModalId.StripeAddPaymentMethodModal);
  }, [openModal]);

  const activeBillingRatesData = React.useMemo(() => {
    return getActiveBillingRatesData(verifierStaffBillingInfo);
  }, [verifierStaffBillingInfo]);

  const renderActiveBillingRates = React.useCallback(() => {
    return (
      <Box sx={dashboardReportAndRevenueBlockBoxStyles} gridTemplateColumns="repeat(3, 1fr)">
        {activeBillingRatesData.map(billingRate => (
          <Box key={billingRate.title} display="flex">
            <ActiveBillingRateItem {...billingRate} />
          </Box>
        ))}
      </Box>
    );
  }, [activeBillingRatesData]);

  if (isUserLoading || billingInfoDataLoading) {
    return <FullscreenLoader />;
  }

  return (
    <React.Fragment>
      {renderActiveBillingRates()}

      <Paper sx={{ mt: 3, py: 2.5, px: 3, borderRadius: 2, overflow: 'hidden' }} variant="outlined">
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1">Payment Methods</Typography>
            <Button color="primary" variant="outlined" onClick={handleAddButtonClick}>
              Add payment method
            </Button>
          </Box>
        </Stack>

        <Divider sx={theme => ({ color: theme.palette.border?.main, mt: 2 })} />

        <StripeCustomerPaymentMethodsList stripeCustomerId={verifierStaff?.stripeCustomerId} />
      </Paper>

      <VerifierStaffAddPaymentMethodModal returnRoute={location.pathname} />
    </React.Fragment>
  );
};

const dashboardReportAndRevenueBlockBoxStyles: SxProp = theme => ({
  display: 'grid',
  gap: 2,

  overflow: 'auto',

  [theme.breakpoints.down('xl')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'none',
  },
});
